import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { MoreHorizontalIcon } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  createTenant,
  dispatchTenantsChanged,
  fetchTenants,
  tenantKeys,
  updateTenant,
} from '@/services/tenantService';
import { parseApiError } from '@/utils/apiError';
import { formatCNPJ, unformatCNPJ } from '@/utils/helpers';
import { empresaFormSchema, type TEmpresaFormData } from './schema';
import type { ITenant } from '@/types/tenant';

export const Route = createFileRoute('/_authenticated/admin/empresas/')({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) {
      return;
    }

    if (context.auth.user?.role !== 'super_admin') {
      toast.error('Você não tem permissão para acessar esta tela.');
      return redirect({ to: '/dashboard' });
    }
  },
  component: Empresas,
});

const emptyEmpresa: TEmpresaFormData = {
  nome: '',
  cnpj: '',
  ativo: true,
};

function Empresas() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isEditing = editingId !== null;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: tenantKeys.list(),
    queryFn: fetchTenants,
  });
  const items = data?.items ?? [];

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<TEmpresaFormData>({
    resolver: zodResolver(empresaFormSchema),
    mode: 'onChange',
    defaultValues: emptyEmpresa,
  });

  const createMutation = useMutation({ mutationFn: createTenant });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { nome: string; ativo: boolean } }) =>
      updateTenant(id, payload),
  });
  const saving = createMutation.isPending || updateMutation.isPending;

  const openCreate = () => {
    setEditingId(null);
    reset(emptyEmpresa);
    setShowForm(true);
  };

  const openEdit = (item: ITenant) => {
    setEditingId(item.id);
    reset({
      nome: item.nome || '',
      cnpj: formatCNPJ(item.cnpj || ''),
      ativo: item.ativo !== false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
    reset(emptyEmpresa);
  };

  const onSubmit = async (formData: TEmpresaFormData) => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          payload: { nome: formData.nome, ativo: formData.ativo },
        });
      } else {
        await createMutation.mutateAsync({
          nome: formData.nome,
          cnpj: unformatCNPJ(formData.cnpj),
        });
      }

      closeForm();
      await queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      dispatchTenantsChanged();
    } catch (err) {
      toast.error(parseApiError(err, editingId ? 'Erro ao atualizar empresa' : 'Erro ao criar empresa'));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Empresas</h1>
        <p className="text-on-surface-variant">Gerencie as empresas (tenants) do sistema.</p>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-col gap-4 border-b border-outline-variant p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-on-surface">Lista de Empresas</h2>
          <Button
            className="bg-success text-on-primary hover:bg-success/90"
            type="button"
            onClick={openCreate}
          >
            Nova empresa
          </Button>
        </div>

        <div className="p-4">
          {isError && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
            >
              {parseApiError(error, 'Erro ao listar empresas')}
            </div>
          )}
          {isLoading && (
            <LoadingSpinner
              text="Carregando..."
              size="small"
            />
          )}
          {!isLoading && (
            <div className="block max-w-full overflow-x-auto overflow-y-hidden">
              <table className="w-max min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-outline-variant text-left">
                    <th className="whitespace-nowrap p-3">Nome</th>
                    <th className="whitespace-nowrap p-3">CNPJ</th>
                    <th className="whitespace-nowrap p-3">Ativo</th>
                    <th className="w-24 whitespace-nowrap p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-outline-variant last:border-0"
                    >
                      <td className="whitespace-nowrap p-3">{item.nome || '-'}</td>
                      <td className="whitespace-nowrap p-3">
                        {item.cnpj ? formatCNPJ(item.cnpj) : '-'}
                      </td>
                      <td className="whitespace-nowrap p-3">{item.ativo !== false ? 'Sim' : 'Não'}</td>
                      <td className="whitespace-nowrap p-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Ações"
                              disabled={saving}
                            >
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-auto min-w-40"
                          >
                            <DropdownMenuGroup>
                              <DropdownMenuItem
                                onSelect={() => {
                                  // defer: dropdown unmount must finish before dialog open, else modal dismisses
                                  setTimeout(() => openEdit(item), 0);
                                }}
                              >
                                Editar
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="p-6 text-center text-on-surface-variant"
                      >
                        Nenhuma empresa encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        modal={true}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar empresa' : 'Nova empresa'}</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <FieldGroup>
              <Field data-invalid={!!errors.nome}>
                <FieldLabel htmlFor="nome">Nome *</FieldLabel>
                <Input
                  id="nome"
                  aria-invalid={!!errors.nome}
                  {...register('nome')}
                />
                <FieldError errors={errors.nome ? [errors.nome] : undefined} />
              </Field>

              <Field data-invalid={!!errors.cnpj}>
                <FieldLabel htmlFor="cnpj">CNPJ *</FieldLabel>
                <Controller
                  control={control}
                  name="cnpj"
                  render={({ field }) => (
                    <Input
                      id="cnpj"
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      value={field.value}
                      disabled={isEditing}
                      readOnly={isEditing}
                      onChange={(e) => field.onChange(formatCNPJ(e.target.value))}
                      onBlur={field.onBlur}
                      aria-invalid={!!errors.cnpj}
                    />
                  )}
                />
                <FieldError errors={errors.cnpj ? [errors.cnpj] : undefined} />
              </Field>

              {isEditing && (
                <Field orientation="horizontal">
                  <Controller
                    control={control}
                    name="ativo"
                    render={({ field }) => (
                      <Checkbox
                        id="ativo"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    )}
                  />
                  <FieldLabel
                    htmlFor="ativo"
                    className="font-normal"
                  >
                    Ativo
                  </FieldLabel>
                </Field>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={saving || !isValid}
                >
                  {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancelar
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
